import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { track } from '@vercel/analytics';
import { supabase } from '../lib/supabase';
import { useSession } from '../hooks/useSession';
import Header from './Header';
import Footer from './Footer';

const SignUpPage: React.FC = () => {
  const { session, isLoaded } = useSession();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  if (isLoaded && session) {
    return <Navigate to="/my-list" replace />;
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    const { data, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // The handle_new_user trigger reads display_name out of
        // raw_user_meta_data when it auto-creates the profile row.
        data: {
          display_name: displayName.trim(),
        },
        // Land confirmed users on the list editor, not the homepage — adding
        // a top five is the whole point of signing up. The URL must be in
        // Supabase's Redirect URLs allowlist or it falls back to the Site URL.
        emailRedirectTo: `${window.location.origin}/my-list`,
      },
    });
    setSubmitting(false);
    if (authError) {
      setError(authError.message);
      return;
    }
    track('Account Created');
    // If email confirmation is required, session will be null until the user
    // confirms. Otherwise (when Supabase is configured without confirmation),
    // they're signed in immediately.
    if (!data.session) {
      setSubmittedEmail(email);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        {submittedEmail ? (
          <div className="card w-full max-w-md p-8 text-center space-y-3">
            <h1 className="font-display text-3xl text-charcoal">Check your email</h1>
            <p className="text-sm text-charcoal-light">
              We sent a confirmation link to <strong>{submittedEmail}</strong>. Click it to
              finish creating your account.
            </p>
            <Link to="/sign-in" className="text-amber hover:underline text-sm">
              Back to sign in
            </Link>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="card w-full max-w-md p-8 space-y-5"
            aria-labelledby="sign-up-heading"
          >
            <h1
              id="sign-up-heading"
              className="font-display text-3xl text-charcoal mb-1"
            >
              Sign up
            </h1>
            <p className="text-sm text-charcoal-light">
              Create an account to publish your top five. Your list shows up at
              your own recommender page once you add five restaurants.
            </p>

            <label className="block text-sm">
              <span className="text-charcoal-light">Your name</span>
              <input
                type="text"
                required
                maxLength={60}
                autoComplete="name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full mt-1 px-4 py-3 bg-cream-warm border border-black/10 rounded-lg text-charcoal focus:outline-none focus:border-amber"
              />
              <span className="text-xs text-charcoal-light/80">
                Shown publicly on your recommender page (e.g. &ldquo;Riley Quinn&rdquo;).
              </span>
            </label>

            <label className="block text-sm">
              <span className="text-charcoal-light">Email</span>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-1 px-4 py-3 bg-cream-warm border border-black/10 rounded-lg text-charcoal focus:outline-none focus:border-amber"
              />
            </label>

            <label className="block text-sm">
              <span className="text-charcoal-light">Password</span>
              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full mt-1 px-4 py-3 bg-cream-warm border border-black/10 rounded-lg text-charcoal focus:outline-none focus:border-amber"
              />
              <span className="text-xs text-charcoal-light/80">At least 8 characters.</span>
            </label>

            {error && (
              <p className="text-sm text-terracotta" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full justify-center disabled:opacity-50"
            >
              {submitting ? 'Creating account…' : 'Create account'}
            </button>

            <p className="text-sm text-charcoal-light text-center">
              Already have an account?{' '}
              <Link to="/sign-in" className="text-amber hover:underline">
                Sign in
              </Link>
            </p>
          </form>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default SignUpPage;
